import React, { useState, useRef, useEffect } from 'react';
import './DatePicker.css';

function DatePicker({ value, onChange, max, min, placeholder, required, id, name }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || '');
  const [displayValue, setDisplayValue] = useState('');
  const [calendarPosition, setCalendarPosition] = useState('below'); // 'above' or 'below'
  const calendarRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      // Handle both string and Date object inputs
      let dateStr = value;
      if (value instanceof Date) {
        dateStr = value.toISOString().split('T')[0];
      } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
        // If it's an object (but not a Date), try to extract value
        dateStr = value.target?.value || value.value || String(value);
      } else if (typeof value !== 'string') {
        dateStr = String(value);
      }
      setSelectedDate(dateStr);
      formatDisplayValue(dateStr);
    } else {
      setSelectedDate('');
      setDisplayValue('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Calculate position based on available space
      if (inputRef.current) {
        const inputRect = inputRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;
        const calendarHeight = 260; // Approximate calendar height (reduced)
        const spacing = 8;
        
        // If not enough space below but enough above, position above
        if (spaceBelow < calendarHeight + spacing && spaceAbove > calendarHeight + spacing) {
          setCalendarPosition('above');
        } else {
          setCalendarPosition('below');
        }
      }
      
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCalendar]);

  const formatDisplayValue = (dateStr) => {
    if (!dateStr) {
      setDisplayValue('');
      return;
    }
    // Parse YYYY-MM-DD format directly without timezone conversion
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      setDisplayValue(`${month}/${day}/${year}`);
    } else {
      // Fallback: try to parse as date
      const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone issues
      if (!isNaN(date.getTime())) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        setDisplayValue(`${month}/${day}/${year}`);
      } else {
        setDisplayValue(dateStr);
      }
    }
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setDisplayValue(input);
    
    // Try to parse MM/DD/YYYY format
    const match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      const [, month, day, year] = match;
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(year, 10);
      
      // Validate date
      const date = new Date(yearNum, monthNum - 1, dayNum);
      if (date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum) {
        // Format date directly as YYYY-MM-DD to avoid timezone issues
        const isoDate = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        setSelectedDate(isoDate);
        if (onChange) {
          // Pass the date value directly (ISO string format: YYYY-MM-DD)
          onChange(isoDate);
        }
      }
    }
  };

  const handleDateSelect = (year, month, day) => {
    // Format date directly as YYYY-MM-DD to avoid timezone issues
    const isoDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(isoDate);
    formatDisplayValue(isoDate);
    setShowCalendar(false);
    if (onChange) {
      // Pass the date value directly (ISO string format: YYYY-MM-DD)
      onChange(isoDate);
    }
  };

  const getCalendarDays = () => {
    let currentMonth, currentYear;
    if (selectedDate) {
      // Parse YYYY-MM-DD format directly
      const match = selectedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        currentYear = parseInt(match[1], 10);
        currentMonth = parseInt(match[2], 10) - 1; // Month is 0-indexed
      } else {
        const baseDate = new Date(selectedDate + 'T12:00:00');
        if (!isNaN(baseDate.getTime())) {
          currentMonth = baseDate.getMonth();
          currentYear = baseDate.getFullYear();
        } else {
          const today = new Date();
          currentMonth = today.getMonth();
          currentYear = today.getFullYear();
        }
      }
    } else {
      const today = new Date();
      currentMonth = today.getMonth();
      currentYear = today.getFullYear();
    }
    
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      // Format date directly as YYYY-MM-DD to avoid timezone issues
      const isoDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isDisabled = 
        (max && isoDate > max) || 
        (min && isoDate < min);
      days.push({ day, date, isoDate, isDisabled });
    }
    
    return { days, month: currentMonth, year: currentYear };
  };

  const { days, month, year } = getCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="date-picker-wrapper" ref={calendarRef}>
      <div className="date-picker-input-group">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setShowCalendar(true)}
          placeholder={placeholder || 'MM/DD/YYYY'}
          required={required}
          className="date-picker-input"
        />
        <button
          type="button"
          className="date-picker-button"
          onClick={() => setShowCalendar(!showCalendar)}
          tabIndex={-1}
        >
          📅
        </button>
      </div>
      
      {showCalendar && (
        <div className={`date-picker-calendar ${calendarPosition === 'above' ? 'calendar-above' : 'calendar-below'}`}>
          <div className="calendar-header">
            <button
              type="button"
              className="calendar-nav"
              onClick={() => {
                // Navigate to previous month
                const newMonth = month === 0 ? 11 : month - 1;
                const newYear = month === 0 ? year - 1 : year;
                // Keep the same day if possible, otherwise use last day of month
                const lastDay = new Date(newYear, newMonth + 1, 0).getDate();
                const day = Math.min(15, lastDay);
                const isoDate = `${newYear}-${String(newMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                setSelectedDate(isoDate);
              }}
            >
              ‹
            </button>
            <div className="calendar-month-year">
              {monthNames[month]} {year}
            </div>
            <button
              type="button"
              className="calendar-nav"
              onClick={() => {
                // Navigate to next month
                const newMonth = month === 11 ? 0 : month + 1;
                const newYear = month === 11 ? year + 1 : year;
                // Keep the same day if possible, otherwise use last day of month
                const lastDay = new Date(newYear, newMonth + 1, 0).getDate();
                const day = Math.min(15, lastDay);
                const isoDate = `${newYear}-${String(newMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                setSelectedDate(isoDate);
              }}
            >
              ›
            </button>
          </div>
          
          <div className="calendar-days-header">
            {dayNames.map(day => (
              <div key={day} className="calendar-day-name">{day}</div>
            ))}
          </div>
          
          <div className="calendar-days">
            {days.map((dayInfo, index) => {
              if (dayInfo === null) {
                return <div key={`empty-${index}`} className="calendar-day empty"></div>;
              }
              
              const isSelected = selectedDate === dayInfo.isoDate;
              // Format today's date directly to avoid timezone issues
              const today = new Date();
              const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              const isToday = dayInfo.isoDate === todayIso;
              
              return (
                <button
                  key={dayInfo.day}
                  type="button"
                  className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${dayInfo.isDisabled ? 'disabled' : ''}`}
                  onClick={() => !dayInfo.isDisabled && handleDateSelect(dayInfo.date.getFullYear(), dayInfo.date.getMonth(), dayInfo.date.getDate())}
                  disabled={dayInfo.isDisabled}
                >
                  {dayInfo.day}
                </button>
              );
            })}
          </div>
          
          <div className="calendar-footer">
            <button
              type="button"
              className="calendar-today-btn"
              onClick={() => {
                const today = new Date();
                const todayYear = today.getFullYear();
                const todayMonth = today.getMonth();
                const todayDay = today.getDate();
                
                if (max) {
                  const maxDate = new Date(max + 'T00:00:00');
                  const maxYear = maxDate.getFullYear();
                  const maxMonth = maxDate.getMonth();
                  const maxDay = maxDate.getDate();
                  
                  // Compare dates properly
                  const todayDate = new Date(todayYear, todayMonth, todayDay);
                  const maxDateObj = new Date(maxYear, maxMonth, maxDay);
                  
                  if (todayDate > maxDateObj) {
                    handleDateSelect(maxYear, maxMonth, maxDay);
                  } else {
                    handleDateSelect(todayYear, todayMonth, todayDay);
                  }
                } else {
                  handleDateSelect(todayYear, todayMonth, todayDay);
                }
              }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;

